// Timeline data types
interface TimelineData {
  id: string;
  type: string;
  data: string[][];
  colCount: number;
  formulaOptions?: {
    commaChar: string;
    decimalChar: string;
    delimiterChar: string;
    currencyChar: string;
    interpretFunctions: boolean;
  };
  altText?: string;
  tabAlign?: string;
}

interface TimelinePoint {
  period: string;
  value: string;
  formattedValue: string;
}

// Configuration for timeline rendering
interface TimelineConfig {
  width: number;
  height: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  pointRadius: number;
  fontSize: string;
  lineStrokeWidth: number;
  maxCharsPerLine?: number;
  minPointSpacing?: number;
}

const DEFAULT_CONFIG: TimelineConfig = {
  width: 600,
  height: 70,
  marginTop: 20,
  marginBottom: 35,
  marginLeft: 40,
  marginRight: 40,
  pointRadius: 4,
  fontSize: '0.688rem',
  lineStrokeWidth: 2,
  maxCharsPerLine: 8,
  minPointSpacing: 50,
};

/**
 * Parses timeline data from the raw data array format
 */
function parseTimelineData(data: string[][]): TimelinePoint[] {
  if (data.length < 2) {
    throw new Error('Timeline data must have at least 2 rows (headers and values)');
  }

  const headers = data[0];
  const values = data[1];
  const points: TimelinePoint[] = [];

  // Skip the first column as it's typically the label
  for (let i = 1; i < headers.length && i < values.length; i++) {
    const period = headers[i];
    const value = values[i];

    // Skip empty values
    if (value && value.trim() !== '') {
      points.push({
        period,
        value,
        formattedValue: formatValue(value),
      });
    }
  }

  return points;
}

/**
 * Wraps text into multiple lines if it's too long
 */
function wrapText(text: string, maxCharsPerLine: number = 8): string[] {
  if (text.length <= maxCharsPerLine) {
    return [text];
  }

  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
      currentLine = currentLine ? currentLine + ' ' + word : word;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Single word is too long, break it
        lines.push(word.substring(0, maxCharsPerLine));
        currentLine = word.substring(maxCharsPerLine);
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}
function formatValue(value: string): string {
  if (!value) return '';

  // Handle the special characters from altText (☻0☺ patterns)
  let formatted = value.replace(/☻0☺/g, '');

  // Clean up any remaining special characters
  formatted = formatted.replace(/[☻☺]/g, '');

  // Handle negative values in parentheses format if needed
  if (formatted.includes('(') && formatted.includes(')')) {
    formatted = '-' + formatted.replace(/[()]/g, '');
  }

  return formatted.trim();
}

/**
 * Generates SVG for a timeline component
 */
function generateTimelineSVG(
  timelineData: TimelineData,
  config: TimelineConfig = DEFAULT_CONFIG
): string {
  const points = parseTimelineData(timelineData.data);

  if (points.length === 0) {
    return '';
  }

  const {
    width,
    height,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    pointRadius,
    fontSize,
    lineStrokeWidth,
    maxCharsPerLine = 8,
    minPointSpacing = 60,
  } = config;

  const contentWidth = width - marginLeft - marginRight;
  const contentHeight = height - marginTop - marginBottom;

  // Calculate positions for points with tighter spacing
  const calculatedSpacing = Math.min(
    minPointSpacing,
    contentWidth / Math.max(points.length - 1, 1)
  );
  const actualWidth = Math.max(
    width,
    marginLeft + marginRight + (points.length - 1) * calculatedSpacing
  );
  const timelineY = marginTop + contentHeight / 2;

  // Build SVG content as a single line to avoid markdown processing issues
  let svgContent = `<svg width="${actualWidth}" height="${height}" xmlns="http://www.w3.org/2000/svg" class="timeline-svg"><defs><style>.timeline-line { stroke: #333; stroke-width: ${lineStrokeWidth}; } .timeline-point { fill: #000; stroke: #fff; stroke-width: 2; } .timeline-point-negative { fill: #000; stroke: #fff; stroke-width: 2; } .timeline-text { font-family: var(--inter); font-size: ${fontSize}; text-anchor: middle; fill: #333; } .timeline-period { font-weight: bold; } .timeline-value { font-weight: normal; }</style></defs>`;

  // Add timeline line
  const lineEndX = marginLeft + (points.length - 1) * calculatedSpacing;
  svgContent += `<line x1="${marginLeft}" y1="${timelineY}" x2="${lineEndX}" y2="${timelineY}" class="timeline-line" />`;

  // Add points and labels
  points.forEach((point, index) => {
    const x = marginLeft + index * calculatedSpacing;
    const isNegative = point.formattedValue.includes('-');

    // Point circle
    svgContent += `<circle cx="${x}" cy="${timelineY}" r="${pointRadius}" class="${
      isNegative ? 'timeline-point-negative' : 'timeline-point'
    }" />`;

    // Period label (above the line) - wrap if needed
    const periodLines = wrapText(point.period, Math.floor(maxCharsPerLine * 0.75)); // Slightly shorter for periods
    periodLines.forEach((line, lineIndex) => {
      const yOffset = timelineY - 15 - (periodLines.length - 1 - lineIndex) * 12;
      svgContent += `<text x="${x}" y="${yOffset}" class="timeline-text timeline-period">${line}</text>`;
    });

    // Value label (below the line) - wrap if needed
    const valueLines = wrapText(point.formattedValue, maxCharsPerLine);
    valueLines.forEach((line, lineIndex) => {
      const yOffset = timelineY + 20 + lineIndex * 12;
      svgContent += `<text x="${x}" y="${yOffset}" class="timeline-text timeline-value">${line}</text>`;
    });
  });

  svgContent += '</svg>';

  return svgContent;
}

/**
 * Main preprocessing function to find and replace timeline data with SVG
 */
export function preprocessTimelineContent(content: string): string {
  const timelineRegex = /%%TL%%(.*?)%%TL%%/gs;

  return content.replace(timelineRegex, (match, jsonData) => {
    try {
      const timelineData: TimelineData = JSON.parse(jsonData.trim());

      // Validate the timeline data
      if (!timelineData.data || !Array.isArray(timelineData.data) || timelineData.data.length < 2) {
        console.error('Invalid timeline data structure');
        return '<div class="timeline-error">Invalid timeline data</div>';
      }

      const svgHtml = generateTimelineSVG(timelineData);

      // Wrap in a div with timeline class for styling
      return `<div class="timeline-container">${svgHtml}</div>`;
    } catch (error) {
      console.error('Error parsing timeline data:', error);
      return '<div class="timeline-error">Error rendering timeline</div>';
    }
  });
}
